













































































































import {
  computed,
  defineComponent,
  PropType,
  ref,
  watch,
  getCurrentInstance
} from "@vue/composition-api";
import ClickOutside from "vue-click-outside";
import OccHeading from "@/components/Atoms/OccHeading/index.vue";
import OccTag from "@/components/Atoms/OccTag/index.vue";
import { useElementSize } from "@/helpers/hooks/useElementSize";
import { sendGaEvent } from "@/helpers/ga-helper";
import { isNotNullOrUndefined } from "@/helpers/type-helper";

export type SelectItem<T = string | number> = { label: string; value: T };

export default defineComponent({
  name: "OccSearchMultiSelectbox",
  components: {
    OccHeading,
    OccTag
  },
  directives: {
    ClickOutside
  },
  props: {
    tagType: {
      type: String as PropType<"circle" | "square">,
      default: "circle"
    },
    tagColor: {
      type: String as PropType<"bar-teal" | "bar-blue">,
      default: "bar-teal"
    },
    placeholder: {
      type: String,
      default: ""
    },
    selectedValues: {
      type: Array as PropType<(string | number)[]>,
      default: () => []
    },
    selectList: {
      type: Array as PropType<SelectItem[]>,
      required: true
    },
    minWidth: {
      type: String,
      default: ""
    },
    width: {
      type: String,
      default: ""
    },
    disabled: {
      type: Boolean,
      default: false
    },
    creatable: {
      type: Boolean,
      default: false
    },
    gaEventCategory: {
      type: String,
      default: ""
    },
    // デバッグ用の一時的な値
    memo: {
      type: String,
      default: ""
    }
  },
  emits: ["change", "create"],
  setup(props, { emit }) {
    const searchText = ref("");
    const searchTextInputRef = ref<HTMLInputElement>();
    const showSelectList = ref<boolean>(false);
    const [selectboxRef, selectboxSize] = useElementSize<HTMLDivElement>();

    const filterdSelectList = computed(() =>
      props.selectList.filter(item => {
        const isSelected = props.selectedValues.includes(item.value);
        if (searchText.value) {
          return !isSelected && item.label.includes(searchText.value);
        }
        return !isSelected;
      })
    );
    const showCreateButton = computed(
      () =>
        props.creatable &&
        !filterdSelectList.value.length &&
        searchText.value &&
        !props.selectList.some(item => item.label.includes(searchText.value))
    );

    const selectedItems = computed(() => {
      return props.selectedValues
        .map(selectedValue => {
          return props.selectList.find(
            selectItem => selectItem.value === selectedValue
          );
        })
        .filter(isNotNullOrUndefined);
      // NOTE: 選択肢リスト (selectList) をAPIで取得している場合
      //       取得に時間がかかって selectedValues が選択肢リスト内に見つからない場合がある。
      //       このとき要素が undefined になり、画面描画にエラーが出るため弾く
    });

    const selectMenuOffsetTop = computed(() => selectboxSize.value.height + 4);

    const instance = getCurrentInstance();

    const clickSelectbox = () => {
      if (props.disabled) {
        return;
      }
      showSelectList.value = true;

      if (instance) {
        sendGaEvent(instance.proxy, {
          gaEventCategory: `${props.gaEventCategory}_セレクトボックス_開く`
        });
      }
    };
    const onClickOutside = () => {
      if (showSelectList.value && instance) {
        sendGaEvent(instance.proxy, {
          gaEventCategory: `${props.gaEventCategory}_セレクトボックス_閉じる`
        });
      }
      showSelectList.value = false;
      searchTextInputRef.value?.blur();
    };
    const changeSelectValue = (item: SelectItem) => {
      const selected = props.selectedValues.includes(item.value);
      const nextSelectedValues = selected
        ? props.selectedValues.filter(review => review !== item.value)
        : [...props.selectedValues, item.value];

      emit("change", nextSelectedValues);
      return nextSelectedValues;
    };
    const onClickSelectItem = (item: SelectItem) => {
      searchText.value = "";
      changeSelectValue(item);

      if (instance) {
        sendGaEvent(instance.proxy, {
          gaEventCategory: `${props.gaEventCategory}_セレクトボックス_アイテム_選択`,
          gaEventLabel: {
            value: item.value,
            label: item.label
          }
        });
      }
    };
    const onDeleteTag = (item: SelectItem) => {
      changeSelectValue(item);

      if (instance) {
        sendGaEvent(instance.proxy, {
          gaEventCategory: `${props.gaEventCategory}_セレクトボックス_アイテム_削除`,
          gaEventLabel: {
            value: item.value,
            label: item.label
          }
        });
      }
    };
    const onInputSearchText = (event: Event) => {
      if (event.target instanceof HTMLInputElement) {
        searchText.value = event.target.value;
      }
    };
    const onBlurSearchTextInput = () => {
      if (showSelectList.value) {
        searchTextInputRef.value?.focus();
      }
    };
    const onEnterTextInput = (event: KeyboardEvent) => {
      if (event.keyCode !== 13) return;

      if (showCreateButton.value) {
        createItem(searchText.value);
      } else {
        onClickOutside();
      }
    };
    const onDeleteTextInput = () => {
      if (!searchText.value && props.selectedValues.length) {
        const value = props.selectedValues[props.selectedValues.length - 1];
        const findResult = props.selectList.find(item => item.value === value);
        const label = findResult ? findResult.label : "";
        const selectItem: SelectItem = { value: value, label: label };
        changeSelectValue(selectItem);
      }
    };
    const createItem = (value: string) => {
      if (instance) {
        sendGaEvent(instance.proxy, {
          gaEventCategory: `${props.gaEventCategory}_セレクトボックス_アイテム_新規作成`,
          gaEventLabel: { label: value }
        });
      }
      emit("create", value);
      searchText.value = "";
    };

    watch([showSelectList, selectedItems], ([currentShowSelectList]) => {
      if (currentShowSelectList) {
        searchTextInputRef.value?.focus();
      } else {
        searchTextInputRef.value?.blur();
      }
    });

    return {
      searchTextInputRef,
      searchText,
      selectboxRef,
      selectboxSize,
      filterdSelectList,
      showCreateButton,
      selectMenuOffsetTop,
      showSelectList,
      selectedItems,
      onClickSelectItem,
      onDeleteTag,
      onInputSearchText,
      clickSelectbox,
      onClickOutside,
      changeSelectValue,
      onBlurSearchTextInput,
      onEnterTextInput,
      onDeleteTextInput,
      createItem
    };
  }
});
